import { useMemo, useState } from 'react';
import {
  useBillableHoursReport,
  useExpensesByTaxReport,
  useProfitAndLossReport,
  useRevenueReport,
  useUtilizationReport,
  useUnbilledWipReport,
} from '../api/queries/reports';
import { downloadBlob } from '../api/client';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { ProjectOptions } from '../components/ProjectOptions';
import { useProjects } from '../api/queries/projects';
import {
  RANGE_PRESETS,
  resolveRange,
  type RangePresetKey,
} from '../utils/reportRange';
import './ReportsPage.css';

type ReportKind =
  | 'unbilled-wip' | 'billable-hours' | 'expenses-by-tax' | 'revenue' | 'utilization'
  | 'profit-and-loss';

// `alsoPerm`: a report that shows two domains needs both — the P&L reads
// payments AND expenses, and the API refuses it without both.
const TABS: { kind: ReportKind; label: string; perm: string; alsoPerm?: string }[] = [
  { kind: 'unbilled-wip',      label: 'Unbilled (WIP)',     perm: 'time_entry.read' },
  { kind: 'billable-hours',    label: 'Billable hours',     perm: 'time_entry.read' },
  { kind: 'expenses-by-tax',   label: 'Expenses (tax)',     perm: 'expense.read' },
  { kind: 'revenue',           label: 'Revenue summary',    perm: 'invoice.read' },
  { kind: 'profit-and-loss',   label: 'Profit & loss',      perm: 'invoice.read', alsoPerm: 'expense.read' },
  { kind: 'utilization',       label: 'Utilization',        perm: 'time_entry.read' },
];

// Revenue is firm-wide on purpose: invoices.project_id is nullable, so a
// project-filtered total would silently drop client-level and multi-project
// invoices. The QuickBooks export is invoice-based and excluded for the same
// reason.
const PROJECT_FILTERABLE: ReadonlySet<ReportKind> = new Set<ReportKind>([
  'unbilled-wip', 'billable-hours', 'expenses-by-tax', 'utilization',
]);

import { formatMoney } from '../utils/money';
import { fmtDate } from '../utils/date';
// Never a bare "Failed to load.": that string hid a 500 from a SQL bug in
// expenses-by-tax-category for the whole life of the report. It cannot show
// the query error (a production 500 body is just "Internal server error"),
// but it does tell a 403 apart from a 500 apart from a dropped connection.
import { apiErrorText } from '../utils/apiErrors';

export function ReportsPage(): JSX.Element {
  const visibleTabs = TABS.filter((t) => can(t.perm) && (!t.alsoPerm || can(t.alsoPerm)));
  const [tab, setTab] = useState<ReportKind>(visibleTabs[0]?.kind ?? 'unbilled-wip');
  const [preset, setPreset] = useState<RangePresetKey>('this-month');
  const [since, setSince] = useState<number | null>(null);
  const [until, setUntil] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<number>(0);
  const { data: projectsData } = useProjects();

  // Date.now() is read here rather than at module load so a tab left open
  // overnight resolves "This month" against today, not yesterday.
  const range = useMemo(
    () => resolveRange(preset, { since, until }, Date.now()),
    [preset, since, until],
  );

  // Hidden on Revenue, so the value must not be sent there either. Kept in
  // state across tab switches so returning to a filtered tab keeps its scope.
  // Without project.read the list comes back empty, so the picker would offer
  // nothing but "All projects" — a filter that cannot filter. Hide it.
  const canFilterByProject = can('project.read');
  const showProjectPicker = canFilterByProject && PROJECT_FILTERABLE.has(tab);
  const scoped = showProjectPicker && projectId > 0 ? projectId : null;
  const projectHidden = canFilterByProject && projectId > 0 && !PROJECT_FILTERABLE.has(tab);

  // The export has to match what is on screen, project scope included, or the
  // CSV quietly disagrees with the table it was downloaded from.
  const onCsv = (path: string) => {
    const scope = scoped ? `&project_id=${scoped}` : '';
    const url = `/api/reports/${path}?since=${range.since}&until=${range.until}${scope}&format=csv`;
    downloadBlob(url, `${path}-${range.since}-${range.until}.csv`).catch(() => {});
  };

  if (visibleTabs.length === 0) {
    return <p className="rp-status">You don't have permission to view reports.</p>;
  }

  return (
    <section className="page-stack page-stack-wide">
      <div className="card rp-toolbar">
        <header className="rp-header">
          <h2>Reports</h2>
          <div className="rp-controls">
            <div className="rp-presets" role="group" aria-label="Date range">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`rp-preset ${preset === p.key ? 'active' : ''}`}
                  aria-pressed={preset === p.key}
                  onClick={() => setPreset(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {showProjectPicker && (
              <label className="rp-field">
                <span>Project</span>
                <select
                  className="form-input rp-project"
                  aria-label="Filter by project"
                  value={projectId}
                  onChange={(e) => setProjectId(Number(e.target.value))}
                >
                  <option value={0}>All projects</option>
                  <ProjectOptions projects={projectsData?.projects ?? []} />
                </select>
              </label>
            )}
          </div>
        </header>

        {/* The typed dates stay, just behind Custom — one click for the common
            case, and the manual entry is still there when a preset does not
            fit. */}
        {preset === 'custom' && (
          <div className="rp-custom">
            <label className="rp-field">
              <span>Since</span>
              <DateInput value={since} onChange={setSince} />
            </label>
            <label className="rp-field">
              <span>Until</span>
              <DateInput value={until} onChange={setUntil} />
            </label>
          </div>
        )}
        <nav className="rp-tabs">
          {visibleTabs.map((t) => (
            <button
              key={t.kind}
              type="button"
              className={`rp-tab ${tab === t.kind ? 'active' : ''}`}
              onClick={() => setTab(t.kind)}
            >
              {t.label}
            </button>
          ))}
          {can('invoice.read') && (
            <button
              type="button"
              className="rp-tab rp-tab-export"
              onClick={() => onCsv('quickbooks-export')}
            >
              QuickBooks export
            </button>
          )}
        </nav>
      </div>

      <div className="card card-tight rp-report-card">
        {/* Hiding the picker is not the same as explaining it: without this,
            picking a project and switching to Revenue reads a firm-wide number
            as if it were scoped to that case. */}
        {projectHidden && (
          <p className="rp-note rp-note-block">
            Firm-wide — this report cannot be filtered by project, because an
            invoice can cover several cases or none.
          </p>
        )}
        {tab === 'unbilled-wip' && <UnbilledWipReport projectId={scoped} onCsv={() => onCsv('unbilled-wip')} />}
        {tab === 'billable-hours' && <BillableHoursReport range={range} projectId={scoped} onCsv={() => onCsv('billable-hours')} />}
        {tab === 'expenses-by-tax' && <ExpensesByTaxReport range={range} projectId={scoped} onCsv={() => onCsv('expenses-by-tax-category')} />}
        {tab === 'revenue' && <RevenueReport range={range} onCsv={() => onCsv('revenue-summary')} />}
        {tab === 'profit-and-loss' && <ProfitAndLossView range={range} onCsv={() => onCsv('profit-and-loss')} />}
        {tab === 'utilization' && <UtilizationReport range={range} projectId={scoped} />}
      </div>
    </section>
  );
}

function CsvButton({ onCsv }: { onCsv: () => void }): JSX.Element {
  return (
    <button type="button" className="btn-charcoal-secondary rp-csv" onClick={onCsv}>
      Download CSV
    </button>
  );
}

function UnbilledWipReport({ projectId, onCsv }: { projectId: number | null; onCsv: () => void }): JSX.Element {
  // No range argument: WIP is a balance as of now, not a flow over a window.
  const q = useUnbilledWipReport(projectId);
  const showExpenses = can('expense.read');
  if (q.isPending) return <p className="rp-status">Loading…</p>;
  if (q.error) return <p className="rp-status rp-error">{apiErrorText(q.error, 'Failed to load.')}</p>;
  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;

  if (rows.length === 0) {
    return (
      <>
        <div className="rp-actions"><CsvButton onCsv={onCsv} /></div>
        <p className="rp-status">Nothing unbilled — every logged hour and approved
          reimbursable expense is on an invoice that has gone out.</p>
      </>
    );
  }

  return (
    <>
      <div className="rp-actions">
        {/* The date range above is deliberately inert here. Say so rather than
            letting someone read a start-of-month number as their whole WIP. */}
        <p className="rp-note">
          Balance as of now — the date range above does not apply. Work sitting on an
          unsent draft invoice still counts as unbilled.
        </p>
        <CsvButton onCsv={onCsv} />
      </div>
      <table className="rp-table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Client</th>
            <th className="rp-numeric">Unbilled hrs</th>
            <th className="rp-numeric">Time value</th>
            {showExpenses && <th className="rp-numeric">Expenses</th>}
            <th className="rp-numeric">Total WIP</th>
            <th className="rp-numeric">Oldest</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.project_id}>
              <td><code>{r.project_case_number}</code></td>
              <td>{r.client_name ?? '—'}</td>
              <td className="rp-numeric">{Number(r.unbilled_hours).toFixed(2)}</td>
              <td className="rp-numeric">{formatMoney(r.unbilled_time_value)}</td>
              {showExpenses && (
                <td className="rp-numeric">{formatMoney(r.unbilled_expense_total)}</td>
              )}
              <td className="rp-numeric">{formatMoney(r.wip_total)}</td>
              <td className="rp-numeric">
                {r.oldest_unbilled_at ? fmtDate(Number(r.oldest_unbilled_at)) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot>
            <tr className="rp-totals">
              <td>Total</td>
              <td>{totals.project_count} project{totals.project_count === 1 ? '' : 's'}</td>
              <td className="rp-numeric">{Number(totals.unbilled_hours).toFixed(2)}</td>
              <td className="rp-numeric">{formatMoney(totals.unbilled_time_value)}</td>
              {showExpenses && (
                <td className="rp-numeric">{formatMoney(totals.unbilled_expense_total)}</td>
              )}
              <td className="rp-numeric">{formatMoney(totals.wip_total)}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </>
  );
}

function BillableHoursReport({ range, projectId, onCsv }: { range: { since: number; until: number }; projectId: number | null; onCsv: () => void }): JSX.Element {
  const q = useBillableHoursReport(range, projectId);
  if (q.isPending) return <p className="rp-status">Loading…</p>;
  if (q.error) return <p className="rp-status rp-error">{apiErrorText(q.error, 'Failed to load.')}</p>;
  const rows = q.data?.rows ?? [];
  return (
    <>
      <div className="rp-actions"><CsvButton onCsv={onCsv} /></div>
      <table className="rp-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Project</th>
            <th className="rp-numeric">Hours</th>
            <th className="rp-numeric">Billable value</th>
            <th className="rp-numeric">Unbilled entries</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.user_id}-${r.project_id}`}>
              <td>{r.user_display_name}</td>
              <td><code>{r.project_case_number}</code></td>
              <td className="rp-numeric">{Number(r.total_hours).toFixed(2)}</td>
              <td className="rp-numeric">{formatMoney(r.billable_value)}</td>
              <td className="rp-numeric">{r.unbilled_entries}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ExpensesByTaxReport({ range, projectId, onCsv }: { range: { since: number; until: number }; projectId: number | null; onCsv: () => void }): JSX.Element {
  const q = useExpensesByTaxReport(range, projectId);
  if (q.isPending) return <p className="rp-status">Loading…</p>;
  if (q.error) return <p className="rp-status rp-error">{apiErrorText(q.error, 'Failed to load.')}</p>;
  const rows = q.data?.rows ?? [];
  return (
    <>
      <div className="rp-actions"><CsvButton onCsv={onCsv} /></div>
      <table className="rp-table">
        <thead>
          <tr>
            <th>Tax category</th>
            <th className="rp-numeric"># expenses</th>
            <th className="rp-numeric">Total</th>
            <th className="rp-numeric">Reimbursable</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.tax_category}>
              <td>{r.tax_category}</td>
              <td className="rp-numeric">{r.expense_count}</td>
              <td className="rp-numeric">{formatMoney(r.total_amount)}</td>
              <td className="rp-numeric">{formatMoney(r.reimbursable_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/**
 * Cash-basis P&L as a statement: income, expenses, net — then the device-sale
 * profit beside it. Device cost is already an expense on its purchase date (the
 * owner's choice), so that section is a memo and never feeds the totals.
 */
function ProfitAndLossView({ range, onCsv }: { range: { since: number; until: number }; onCsv: () => void }): JSX.Element {
  const q = useProfitAndLossReport(range);
  if (q.isPending) return <p className="rp-status">Loading…</p>;
  if (q.error) return <p className="rp-status rp-error">{apiErrorText(q.error, 'Failed to load.')}</p>;
  const p = q.data!;
  const loss = Number(p.net_income) < 0;
  const line = (label: string, amount: string, cls = '') => (
    <tr className={cls}>
      <td>{label}</td>
      <td className="rp-numeric">{formatMoney(amount)}</td>
    </tr>
  );

  return (
    <>
      <div className="rp-actions"><CsvButton onCsv={onCsv} /></div>
      <p className="rp-pnl-note">
        Cash basis — income is the {p.payment_count} payment{p.payment_count === 1 ? '' : 's'} received
        in this period; expenses are those dated in it, plus contractor payments marked paid.
      </p>
      <table className="rp-table rp-pnl">
        <tbody>
          <tr className="rp-pnl-section"><th colSpan={2}>Income</th></tr>
          {line('Professional services', p.income.services)}
          {line('Product sales', p.income.product_sales)}
          {line('Reimbursed expenses', p.income.reimbursed_expenses)}
          {line('Other income', p.income.other)}
          {line('Equipment sold', p.income.equipment_sales)}
          {line('Total income', p.income.total, 'rp-pnl-total')}

          <tr className="rp-pnl-section"><th colSpan={2}>Expenses</th></tr>
          {p.expenses.by_category.map((e) => (
            <tr key={e.category}>
              <td>{e.category}</td>
              <td className="rp-numeric">{formatMoney(e.amount)}</td>
            </tr>
          ))}
          {line('Contractor payments', p.expenses.contractor_payments)}
          {line('Total expenses', p.expenses.total, 'rp-pnl-total')}

          <tr className={`rp-pnl-net${loss ? ' rp-warn' : ''}`}>
            <td>{loss ? 'Net loss' : 'Net income'}</td>
            <td className="rp-numeric">{formatMoney(p.net_income)}</td>
          </tr>
        </tbody>
      </table>
      {Number(p.sales_tax_collected) !== 0 && (
        <p className="rp-pnl-note">
          Sales tax collected: {formatMoney(p.sales_tax_collected)} — owed to the state, so not counted as income.
        </p>
      )}

      <h3 className="rp-pnl-heading">Device Sales</h3>
      {p.device_sales.length === 0 ? (
        <p className="rp-status">No devices invoiced in this period.</p>
      ) : (
        <>
          <p className="rp-pnl-note">
            Each device invoiced in this period — and any equipment sold outside an invoice — against
            what it cost. Its cost is already an expense on its purchase date, so these figures are
            not added to the totals above.
          </p>
          <table className="rp-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Invoice</th>
                <th>Date</th>
                <th className="rp-numeric">Sold for</th>
                <th className="rp-numeric">Cost</th>
                <th className="rp-numeric">Profit</th>
              </tr>
            </thead>
            <tbody>
              {p.device_sales.map((d) => (
                <tr key={`${d.asset_id}-${d.invoice_id ?? 'direct'}`}>
                  <td>{d.asset_name}</td>
                  <td>
                    {d.invoice_number === null
                      ? 'Sold directly'
                      : `${d.invoice_number}${d.invoice_paid ? '' : ' (unpaid)'}`}
                  </td>
                  <td>{fmtDate(d.issue_date)}</td>
                  <td className="rp-numeric">{formatMoney(d.sale_amount)}</td>
                  <td className="rp-numeric">{formatMoney(d.cost)}</td>
                  <td className={`rp-numeric${Number(d.profit) < 0 ? ' rp-warn' : ''}`}>{formatMoney(d.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function RevenueReport({ range, onCsv }: { range: { since: number; until: number }; onCsv: () => void }): JSX.Element {
  const q = useRevenueReport(range);
  if (q.isPending) return <p className="rp-status">Loading…</p>;
  if (q.error) return <p className="rp-status rp-error">{apiErrorText(q.error, 'Failed to load.')}</p>;
  const rows = q.data?.rows ?? [];
  return (
    <>
      <div className="rp-actions"><CsvButton onCsv={onCsv} /></div>
      <table className="rp-table">
        <thead>
          <tr>
            <th>Month</th>
            <th className="rp-numeric"># invoices</th>
            <th className="rp-numeric">Billed</th>
            <th className="rp-numeric">Paid</th>
            <th className="rp-numeric">Outstanding</th>
            <th className="rp-numeric">Overdue</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month}>
              <td>{r.month}</td>
              <td className="rp-numeric">{r.invoice_count}</td>
              <td className="rp-numeric">{formatMoney(r.billed_total)}</td>
              <td className="rp-numeric">{formatMoney(r.paid_total)}</td>
              <td className="rp-numeric">{formatMoney(r.outstanding_total)}</td>
              <td className="rp-numeric rp-warn">{formatMoney(r.overdue_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function UtilizationReport(
  { range, projectId }: { range: { since: number; until: number }; projectId: number | null },
): JSX.Element {
  const q = useUtilizationReport(range, projectId);
  if (q.isPending) return <p className="rp-status">Loading…</p>;
  if (q.error) return <p className="rp-status rp-error">{apiErrorText(q.error, 'Failed to load.')}</p>;
  const rows = q.data?.rows ?? [];
  const target = q.data?.target_hours ?? 0;
  return (
    <>
      {/* target_hours stays firm-wide capacity, so a filtered percentage is
          "share of capacity spent on this case", not utilization against a
          project target. Cheaper to say it than to mislabel the column. */}
      {projectId !== null && (
        <p className="rp-note rp-note-block">
          Filtered to one case. Hours count only that case; the target is still
          full capacity, so the percentage is the share of capacity spent on it.
        </p>
      )}
      <p className="rp-meta">Target: {target}h over the range (assumes 8h × business days).</p>
      <table className="rp-table">
        <thead>
          <tr>
            <th>User</th>
            <th className="rp-numeric">Hours logged</th>
            <th className="rp-numeric">Target</th>
            <th className="rp-numeric">Utilization</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id}>
              <td>{r.user_display_name}</td>
              <td className="rp-numeric">{Number(r.hours_logged).toFixed(2)}</td>
              <td className="rp-numeric">{r.target_hours}</td>
              <td className="rp-numeric">{Number(r.utilization_pct).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
